// 189 §H — WRITE TO ONE CLIENT WITH A TEMPLATE, FROM THEIR SCREEN.
//
// The person's own pane offers it (EntityList's `detailActions` slot, ecfg's
// `cl`); this composes a firm template (or an ad-hoc body) merged for this
// client — the same merge fields the campaigns use, rendered by the server
// through the same engine — shows the rendered result, and creates an Outlook
// DRAFT in the user's own mailbox (POST /clients/:id/mail-draft), opened in a
// new tab. RULING 189 (7): consent is NOT consulted — this is correspondence,
// and a client who refused campaigns can still be written to; the route says
// so, and this modal asks nothing about consent.
//
// SESSION_RULES 3b: a NEW file under src/components/modals the brief did not
// name — DEVIATION, listed first in the report (§H names the feature and no
// surface; ModalLayout is the shell, the composer is this).
//
// Zero-warning by construction: `import React`, callbacks by React's
// Dispatch, `t` through TFn, no `any`.
import React, { useEffect, useState } from "react";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import type { Dispatch } from "react";
import { api } from "../../api";
import { useTranslation } from "../../i18n";
import { MERGE_TOKEN_NAMES } from "../../lib/collectiveSegments.mjs";
import MenuSelect from "../MenuSelect";
import { ModalLayout } from "../SharedUI";
import { CT, FS, RAD } from "../../lib/tokens";
import type { TFn } from "../../lib/taxonomyTypes";

// The preview is HTML the server composed from the firm's template; it is shown
// inside a sandbox="" iframe (html-sinks-sandboxed), never in the app's own origin.
function previewDoc(body: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,sans-serif;font-size:13px;color:${CT.c_222};background:${CT.c_fff};margin:8px}img{max-width:100%;height:auto}</style></head><body>${body}</body></html>`;
}

type Template = Readonly<{ id: number; name: string; isActive?: boolean }>;
type Preview = Readonly<{ to: string; subject: string; body: string }>;
type Draft = Readonly<{ ok: boolean; to: string; webLink?: string | null; stubbed?: boolean }>;
type Props = Readonly<{
  client: Readonly<{ id: number; name?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null }>;
  onClose: Dispatch<void>;
  toast: NonNullable<Window["lxToast"]>;
}>;

const CLIENT_TOKENS: ReadonlySet<string> = new Set(["firstName", "lastName", "fullName", "companyName", "title", "email"]);

export default function ClientMailTemplateModal({ client, onClose, toast }: Props) {
  const { t } = useTranslation() as { t: TFn };
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [adhoc, setAdhoc] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<Template[]>("/bulk-mail-templates").then((rows) => { setTemplates(Array.isArray(rows) ? rows.filter((x) => x.isActive !== false) : []); }).catch(() => { setTemplates([]); });
  }, []);
  const body = () => (templateId ? { templateId: Number(templateId) } : { adhocBody: adhoc });
  const ready = !!subject.trim() && (!!templateId || !!adhoc.trim());
  const post = (extra: Record<string, unknown>) =>
    api<Preview & Draft>(`/clients/${String(client.id)}/mail-draft`, { method: "POST", body: JSON.stringify({ subject, ...body(), ...extra }) });
  const doPreview = async () => {
    setBusy(true);
    try { setPreview(await post({ preview: true })); } catch (e) { toast(e instanceof Error ? e.message : String(e), "error"); }
    setBusy(false);
  };
  const doDraft = async () => {
    setBusy(true);
    try {
      const d = await post({});
      if (d.webLink) globalThis.open(d.webLink, "_blank", "noopener");
      // 209 §7 — one client's draft: its own sentence (the mailing's counted Bcc recipients, and printed « {n} » as written)
      toast(t("clientMail.draft_created", { fallback: "Outlook draft created." }), "success");
      onClose();
    } catch (e) { toast(e instanceof Error ? e.message : String(e), "error"); }
    setBusy(false);
  };
  const joined = [client.firstName, client.lastName].filter(Boolean).join(" ");
  const who = joined === "" ? (client.name ?? String(client.id)) : joined;
  const mergeHint = MERGE_TOKEN_NAMES.filter((n: string) => CLIENT_TOKENS.has(n)).map((n: string) => "{" + n + "}").join(" ");
  return (
    <ModalLayout
      title={t("clientMail.title", { name: who, fallback: "Write to " + who })}
      onClose={() => { onClose(); }}
      footer={
        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={() => { onClose(); }}>{t("common.cancel", { fallback: "Cancel" })}</button>
          <button type="button" className="btn btn-ghost" disabled={!ready || busy} onClick={() => { void doPreview(); }}>{t("clientMail.preview", { fallback: "Preview" })}</button>
          <button type="button" className="modal-save" disabled={!ready || busy} onClick={() => { void doDraft(); }}>{t("clientMail.draft", { fallback: "Create Outlook draft" })}</button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ fontSize: FS.s10_5, color: "var(--tm)" }}>{t("clientMail.to", { fallback: "To" })}: {client.email ?? t("clientMail.no_email", { fallback: "(no e-mail address)" })}</div>
        <input {...NO_AUTOFILL} className={noAutofillClass("field-input")} placeholder={t("campaigns.subject", { fallback: "Subject" })} value={subject} onChange={(e) => { setSubject(e.target.value); setPreview(null); }} />
        <MenuSelect
          className="field-input"
          value={templateId}
          onChange={(v) => { setTemplateId(v); setPreview(null); }}
          options={[["", t("bulkMail.body_adhoc", { fallback: "Ad-hoc body (this mailing only)" })] as const, ...templates.map((x) => [String(x.id), x.name] as const)]}
        />
        {!templateId && (
          <textarea {...NO_AUTOFILL} className={noAutofillClass("field-input")} rows={6} style={{ resize: "vertical" }} value={adhoc} onChange={(e) => { setAdhoc(e.target.value); setPreview(null); }} />
        )}
        <div style={{ fontSize: FS.s10_5, color: "var(--tm)" }}>{t("bulkMail.merge_hint", { fallback: "Merge fields:" })} {mergeHint}</div>
        {preview && (
          <div style={{ border: "1px solid var(--br)", borderRadius: RAD.r6, padding: 8, background: "var(--bg2)", fontSize: FS.s11 }}>
            <div style={{ fontWeight: 600 }}>{preview.subject}</div>
            <iframe
              sandbox=""
              srcDoc={previewDoc(preview.body)}
              style={{ marginTop: 4, width: "100%", height: 240, border: "1px solid var(--br)", borderRadius: RAD.r6, background: CT.c_fff }}
              title={t("email_drawer.body_iframe_title", { fallback: "email body" })}
            />
          </div>
        )}
      </div>
    </ModalLayout>
  );
}
